import './EffortImpactView.scss';
import { KeyboardEvent, MouseEvent, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { EffortImpactMeta, FUNCTION_ORDER, Product } from './product-roadmap-constants';
import { EffortImpactScales } from './EffortImpactScales';
import {
  EFFORT_BAND_HINTS,
  packPoints,
  primaryFunction,
  QUADRANT_LINE,
  quadrantOf,
  QUADRANTS,
  QuadrantType,
  SCORE_MAX,
  SCORE_MIN,
  ScoredProduct,
  scoredProducts,
  sortForQuadrantList,
  unscoredProducts
} from './effort-impact-utils';

// Drawn in a fixed viewBox and scaled to the panel's width, so the chart needs no measuring and
// keeps its proportions from a phone to a wide monitor. Scores 1..10 sit at the centre of ten
// equal cells (the domain is padded half a point each side), so a 1 or a 10 never touches the
// frame.
const VIEW_W = 760;
const VIEW_H = 560;
const MARGIN = { left: 64, right: 20, top: 20, bottom: 58 };
const PLOT_W = VIEW_W - MARGIN.left - MARGIN.right;
const PLOT_H = VIEW_H - MARGIN.top - MARGIN.bottom;
const DOMAIN = SCORE_MAX - SCORE_MIN + 1;
const xOf = (effort: number) => MARGIN.left + ((effort - SCORE_MIN + 0.5) / DOMAIN) * PLOT_W;
const yOf = (impact: number) =>
  MARGIN.top + PLOT_H - ((impact - SCORE_MIN + 0.5) / DOMAIN) * PLOT_H;
const TICKS = Array.from({ length: DOMAIN }, (_, i) => SCORE_MIN + i);
const DOT_RADIUS = 7.5;
const DOT_SPACING = 17;

// Each quadrant's name sits in its own corner of the plot; the same corner is marked on its
// card under the chart, and both carry the quadrant's color.
const QUADRANT_LABEL_POSITIONS: {
  key: QuadrantType;
  x: number;
  y: number;
  anchor: 'start' | 'end';
}[] = [
  { key: 'quick-win', x: MARGIN.left + 10, y: MARGIN.top + 20, anchor: 'start' },
  { key: 'big-bet', x: MARGIN.left + PLOT_W - 10, y: MARGIN.top + 20, anchor: 'end' },
  { key: 'fill-in', x: MARGIN.left + 10, y: MARGIN.top + PLOT_H - 10, anchor: 'start' },
  { key: 'caution', x: MARGIN.left + PLOT_W - 10, y: MARGIN.top + PLOT_H - 10, anchor: 'end' }
];

// Which cell of the small 2x2 icon on each quadrant card is filled: chart order, reading
// left-to-right then top-to-bottom.
const GLYPH_CELL: Record<QuadrantType, number> = {
  'quick-win': 0,
  'big-bet': 1,
  'fill-in': 2,
  caution: 3
};

const Chevron = () => (
  <svg className='effort-impact-chevron' viewBox='0 0 16 16' aria-hidden='true'>
    <path
      d='M4 6l4 4 4-4'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

interface HoverState {
  product: ScoredProduct;
  x: number;
  y: number;
  flipLeft: boolean;
}

interface EffortImpactViewProps {
  products: Product[];
  // In-scope (assessable) products before any filter -- the chart says it is filtered only when
  // a filter has dropped one of these, never because of the default "hide Deployed" status.
  totalInScope: number;
  meta: EffortImpactMeta | undefined;
  functionColorMap: Record<string, string>;
  onOpenCard: (product: Product) => void;
  onResetFilters: () => void;
}

export const EffortImpactView = ({
  products,
  totalInScope,
  meta,
  functionColorMap,
  onOpenCard,
  onResetFilters
}: EffortImpactViewProps) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  // Functions switched off in the legend. Empty = everything shown.
  const [hiddenFunctions, setHiddenFunctions] = useState<ReadonlySet<string>>(new Set());
  const [quadrantsOpen, setQuadrantsOpen] = useState(true);

  const scored = useMemo(() => scoredProducts(products), [products]);
  const unscored = useMemo(() => unscoredProducts(products), [products]);
  const packed = useMemo(() => packPoints(scored), [scored]);
  const quadrantLists = useMemo(
    () =>
      QUADRANTS.map(quadrant => ({
        quadrant,
        list: scored
          .filter(p => quadrantOf(p.assessment) === quadrant.key)
          .sort(sortForQuadrantList)
      })),
    [scored]
  );

  const functionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    scored.forEach(p => {
      const f = primaryFunction(p);
      counts.set(f, (counts.get(f) ?? 0) + 1);
    });
    return counts;
  }, [scored]);
  const legendFunctions = useMemo(
    () =>
      [...functionCounts.keys()].sort((a, b) => {
        const ia = FUNCTION_ORDER.indexOf(a);
        const ib = FUNCTION_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
      }),
    [functionCounts]
  );

  const isShown = (p: Product) => !hiddenFunctions.has(primaryFunction(p));

  // First click while everything is showing isolates that function; later clicks add or remove
  // one at a time; switching the last one off shows everything again.
  const toggleFunction = (fn: string) => {
    setHiddenFunctions(prev => {
      if (prev.size === 0) return new Set(legendFunctions.filter(f => f !== fn));
      const next = new Set(prev);
      if (next.has(fn)) {
        next.delete(fn);
      } else {
        next.add(fn);
      }
      return next.size >= legendFunctions.length ? new Set() : next;
    });
  };

  // The tooltip opens to the right of the pointer, or to the left on the chart's right half so
  // it never runs off the panel. Decided here, from the event, rather than by reading the ref
  // during render.
  const showTip = (product: ScoredProduct, clientX: number, clientY: number) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    const x = clientX - box.left;
    setHover({ product, x, y: clientY - box.top, flipLeft: x > box.width / 2 });
  };
  const onDotKey = (event: KeyboardEvent, product: ScoredProduct) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenCard(product);
    }
  };

  const isFiltered = scored.length + unscored.length < totalInScope;
  const inScope = meta?.inScope ?? scored.length + unscored.length;

  // Data published before product-brain #81 carries no scores at all; say so plainly instead of
  // drawing an empty chart that reads like "nothing is in progress".
  if (scored.length === 0 && unscored.length === 0 && !isFiltered) {
    return (
      <div className='effort-impact-panel effort-impact-empty'>
        <h2 className='effort-impact-heading'>No Effort × Impact scores published yet</h2>
        <p>
          Scores come from the product roadmap&apos;s Effort × Impact table. They appear here after
          the next roadmap publish that includes them.
        </p>
      </div>
    );
  }

  return (
    <div className='effort-impact'>
      {meta?.draft && (
        <div className='effort-impact-banner' role='status'>
          <span className='effort-impact-banner-pill'>Draft</span>
          <p>
            <strong>These scores are under team review.</strong> Positions may change once the
            product team has reviewed them.
          </p>
        </div>
      )}

      {isFiltered && (
        <p className='effort-impact-filter-note'>
          Filters set on the Roadmap tab apply here too.{' '}
          <button type='button' className='effort-impact-link' onClick={onResetFilters}>
            Clear filters
          </button>
        </p>
      )}

      <div className='effort-impact-work'>
        <div className='effort-impact-panel'>
          <div className='effort-impact-chart-head'>
            <h2 className='effort-impact-heading'>Impact against remaining effort</h2>
            <span className='effort-impact-count'>
              {meta ? `${meta.scored} of ${inScope}` : scored.length} active products scored
            </span>
          </div>

          <div className='effort-impact-chart' ref={wrapRef}>
            <svg
              viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
              role='img'
              aria-label='Scatter chart of product impact against remaining effort'
            >
              <rect
                className='ei-plot'
                x={MARGIN.left}
                y={MARGIN.top}
                width={PLOT_W}
                height={PLOT_H}
              />
              {TICKS.map(v => (
                <g key={v}>
                  <line
                    className='ei-grid'
                    x1={xOf(v)}
                    x2={xOf(v)}
                    y1={MARGIN.top}
                    y2={MARGIN.top + PLOT_H}
                  />
                  <line
                    className='ei-grid'
                    x1={MARGIN.left}
                    x2={MARGIN.left + PLOT_W}
                    y1={yOf(v)}
                    y2={yOf(v)}
                  />
                  <text
                    className='ei-tick'
                    x={xOf(v)}
                    y={MARGIN.top + PLOT_H + 16}
                    textAnchor='middle'
                  >
                    {v}
                  </text>
                  <text className='ei-tick' x={MARGIN.left - 10} y={yOf(v) + 4} textAnchor='end'>
                    {v}
                  </text>
                </g>
              ))}
              {EFFORT_BAND_HINTS.map(({ at, label }) => (
                <text
                  key={label}
                  className='ei-band'
                  x={xOf(at)}
                  y={MARGIN.top + PLOT_H + 30}
                  textAnchor='middle'
                >
                  {label}
                </text>
              ))}
              <line
                className='ei-divider'
                x1={xOf(QUADRANT_LINE)}
                x2={xOf(QUADRANT_LINE)}
                y1={MARGIN.top}
                y2={MARGIN.top + PLOT_H}
              />
              <line
                className='ei-divider'
                x1={MARGIN.left}
                x2={MARGIN.left + PLOT_W}
                y1={yOf(QUADRANT_LINE)}
                y2={yOf(QUADRANT_LINE)}
              />
              {QUADRANT_LABEL_POSITIONS.map(({ key, x, y, anchor }) => (
                <text
                  key={key}
                  className={`ei-quadrant ei-quadrant-${key}`}
                  x={x}
                  y={y}
                  textAnchor={anchor}
                >
                  {QUADRANTS.find(q => q.key === key)?.label}
                </text>
              ))}
              <text
                className='ei-axis'
                x={MARGIN.left + PLOT_W / 2}
                y={VIEW_H - 14}
                textAnchor='middle'
              >
                Effort remaining
              </text>
              <text
                className='ei-axis'
                x={18}
                y={MARGIN.top + PLOT_H / 2}
                textAnchor='middle'
                transform={`rotate(-90 18 ${MARGIN.top + PLOT_H / 2})`}
              >
                Impact
              </text>

              {packed.map(({ product, dx, dy }) => (
                <g
                  key={product.name}
                  className={cn('ei-dot', {
                    'ei-dot-dim': !isShown(product),
                    'ei-dot-hot': hover?.product.name === product.name
                  })}
                  role='button'
                  tabIndex={0}
                  aria-label={`${product.name}: impact ${product.assessment.impact}, effort ${product.assessment.effort}`}
                  onMouseEnter={(e: MouseEvent) => showTip(product, e.clientX, e.clientY)}
                  onMouseMove={(e: MouseEvent) => showTip(product, e.clientX, e.clientY)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={e => {
                    const r = e.currentTarget.getBoundingClientRect();
                    showTip(product, r.right, r.top);
                  }}
                  onBlur={() => setHover(null)}
                  onClick={() => onOpenCard(product)}
                  onKeyDown={e => onDotKey(e, product)}
                >
                  <circle
                    cx={xOf(product.assessment.effort) + dx * DOT_SPACING}
                    cy={yOf(product.assessment.impact) + dy * DOT_SPACING}
                    r={DOT_RADIUS}
                    fill={functionColorMap[primaryFunction(product)]}
                  />
                </g>
              ))}
            </svg>

            {hover && (
              <div
                className={cn('effort-impact-tip', {
                  'effort-impact-tip-left': hover.flipLeft
                })}
                style={{ left: hover.x, top: hover.y }}
              >
                <div className='effort-impact-tip-title'>{hover.product.name}</div>
                <div className='effort-impact-tip-meta'>
                  <span>{hover.product.life}</span>
                  <span>{hover.product.target}</span>
                </div>
                <div className='effort-impact-tip-scores'>
                  Impact {hover.product.assessment.impact} · Effort{' '}
                  {hover.product.assessment.effort}
                </div>
                <div className='effort-impact-tip-desc'>
                  {hover.product.desc || hover.product.oneLiner}
                </div>
                <div className='effort-impact-tip-hint'>Click to open</div>
              </div>
            )}
          </div>
          <p className='effort-impact-note'>
            Products with the same scores sit side by side around their shared point. Hover a dot
            for detail; click it to open the product.
          </p>
        </div>

        <div className='effort-impact-side'>
          <div className='effort-impact-panel'>
            <h2 className='effort-impact-heading'>Function</h2>
            <div className='effort-impact-legend'>
              {legendFunctions.map(fn => (
                <button
                  key={fn}
                  type='button'
                  aria-pressed={!hiddenFunctions.has(fn)}
                  onClick={() => toggleFunction(fn)}
                >
                  <span
                    className='effort-impact-swatch'
                    style={{ background: functionColorMap[fn] }}
                  />
                  <span>{fn}</span>
                  <span className='effort-impact-legend-count'>{functionCounts.get(fn)}</span>
                </button>
              ))}
            </div>
            <div className='effort-impact-legend-foot'>
              <button
                type='button'
                className='effort-impact-link'
                onClick={() => setHiddenFunctions(new Set())}
              >
                Show all
              </button>
              <span>Colored by primary function</span>
            </div>
          </div>
          {meta?.scales && <EffortImpactScales scales={meta.scales} />}
        </div>
      </div>

      <div className='effort-impact-panel'>
        <div className='effort-impact-quadrants-head'>
          <button
            type='button'
            className='effort-impact-toggle'
            aria-expanded={quadrantsOpen}
            aria-controls='effort-impact-quadrants'
            onClick={() => setQuadrantsOpen(open => !open)}
          >
            <Chevron />
            By quadrant
          </button>
          {!quadrantsOpen && (
            <div className='effort-impact-quadrant-summary'>
              {quadrantLists.map(({ quadrant, list }) => (
                <span key={quadrant.key} className={`ei-q-${quadrant.key}`}>
                  {list.length} {quadrant.label.toLowerCase()}
                </span>
              ))}
            </div>
          )}
        </div>
        <div id='effort-impact-quadrants' hidden={!quadrantsOpen}>
          <div className='effort-impact-quadrants'>
            {quadrantLists.map(({ quadrant, list }) => (
              <section
                key={quadrant.key}
                className={`effort-impact-quadrant ei-q-${quadrant.key}`}
                aria-label={quadrant.label}
              >
                <div className='effort-impact-quadrant-head'>
                  <span className='effort-impact-glyph' aria-hidden='true'>
                    {[0, 1, 2, 3].map(cell => (
                      <i
                        key={cell}
                        className={cn({ 'is-on': cell === GLYPH_CELL[quadrant.key] })}
                      />
                    ))}
                  </span>
                  <div>
                    <h3 className='effort-impact-quadrant-title'>{quadrant.label}</h3>
                    <p className='effort-impact-quadrant-sub'>{quadrant.description}</p>
                  </div>
                  <span className='effort-impact-quadrant-count'>{list.length}</span>
                </div>
                {list.length === 0 ? (
                  <p className='effort-impact-quadrant-none'>None right now.</p>
                ) : (
                  <>
                    <div className='effort-impact-quadrant-cols' aria-hidden='true'>
                      <span>Product</span>
                      <span>Impact</span>
                      <span>Effort</span>
                    </div>
                    <ul className='effort-impact-quadrant-list'>
                      {list.map(p => (
                        <li key={p.name}>
                          <button
                            type='button'
                            className={cn({ 'ei-dim': !isShown(p) })}
                            aria-label={`${p.name}: impact ${p.assessment.impact}, effort ${p.assessment.effort}`}
                            onClick={() => onOpenCard(p)}
                          >
                            <span
                              className='effort-impact-swatch'
                              style={{ background: functionColorMap[primaryFunction(p)] }}
                            />
                            <span>{p.name}</span>
                            <span className='effort-impact-score'>{p.assessment.impact}</span>
                            <span className='effort-impact-score'>{p.assessment.effort}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            ))}
          </div>
          <p className='effort-impact-note'>
            {unscored.length > 0
              ? `Not yet scored: ${unscored.map(p => p.name).join(', ')}.`
              : `All ${scored.length} active products shown are scored.`}{' '}
            Deployed and paused products are not on this chart.
          </p>
        </div>
      </div>
    </div>
  );
};
